import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { bridge, isNotNil } from '../../utils';
import { AdAction } from '../../utils/eventLogger/adLogger/types';
import type { FlattenedItem } from './useFlattenedItems';

/**
 * AtomicRenderer 전용 viewability 훅.
 *
 * FlatList 빌트인 viewability(`viewabilityConfig` / `onViewableItemsChanged`)는
 * 높이 편차가 큰 heterogeneous 리스트에서 미측정 cell을 평균 높이로 추정하는
 * fallback과 onLayout 이후 재평가 누락 때문에 임계치가 pixel 단위로 보장되지
 * 않는다. 대신 onLayout 실측 `itemHeights`와 JS 측 `scrollY`로 직접 계산한다.
 *
 * 재평가 트리거:
 * - onScroll: worklet에서 `updateScrollY`로 브리지
 * - item onLayout / list onLayout: 외부에서 `scheduleCheck` 직접 호출
 * - displayItems 변경(pagination / filter / PROXY 로드): 내부 useEffect 자동
 *
 * 모든 트리거는 80ms coalescing으로 묶여 `runCheck`을 1회 실행한다.
 */

const COALESCE_MS = 80;

type DisplayEventName = 'DISPLAY_GOODS' | 'DISPLAY_PRIMARY_CARD';

interface UseAtomicGoodsViewabilityParams {
  displayItems: FlattenedItem[];
  /** 각 index의 실측 높이. item onLayout에서 갱신. */
  itemHeights: RefObject<{ [key: number]: number }>;
  /** 리스트 viewport 높이. list onLayout에서 갱신. */
  viewportHeightRef: RefObject<{ height: number }>;
  /** 화면 이름(SCREEN) — analytics payload에 병합. */
  screen?: string;
  /** 로깅 전체 비활성화 (autoScroll gate 계산은 계속 수행). */
  logDisabled?: boolean;
  /** viewability 임계치 (0~1). 이 비율 이상 보이면 DISPLAY 이벤트 전송. */
  thresholdRatio: number;
  /** screens 쿼리의 fetch 진행 상태. awaiting unblock 시점을 데이터 안정 시점에
   * 묶기 위함(고정 1.5초 timer가 slow API에서 fetch 완료 전 풀려 emit이 새는 문제). */
  isFetching: boolean;
  /** scrollToFilterList 보류 상태. fetch 완료 후 scroll dispatch 까지 awaiting 유지. */
  isPendingScroll?: boolean;
}

interface UseAtomicGoodsViewabilityReturn {
  /** 현재 임계치 이상 보이는 component kind 항목의 componentId (배너 autoScroll gate용). */
  visibleComponentIds: Set<number>;
  /** 필터 변경/pull-to-refresh 직후 데이터 swap + auto-scroll 애니메이션이
   * 끝나기 전까지 true. 자식(GoodsCarousel 등)이 자체 emit을 보류하기 위한 신호. */
  isAwaitingDataChange: boolean;
  /** 재평가 예약. 80ms coalescing 후 실행. */
  scheduleCheck: () => void;
  /** onScroll worklet → JS 브리지용. 최신 scrollY를 반영하고 check 예약. */
  updateScrollY: (y: number) => void;
  /** display history 초기화 */
  resetDisplayHistory: () => void;
}

/** Set 동치성 비교 — setState 불필요한 리렌더 억제용. */
const setsEqual = <T>(a: Set<T>, b: Set<T>): boolean => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

export const useAtomicGoodsViewability = ({
  displayItems,
  itemHeights,
  viewportHeightRef,
  screen,
  logDisabled,
  thresholdRatio,
  isFetching,
  isPendingScroll,
}: UseAtomicGoodsViewabilityParams): UseAtomicGoodsViewabilityReturn => {
  const scrollYRef = useRef(0);
  const displayHistoryRef = useRef<Set<string>>(new Set());
  // 필터 변경 직후부터 scrollToFilterList 애니메이션이 끝나기 전까지 emit을 차단.
  // 차단을 해제할 때 마지막으로 한 번 더 평가하여 post-scroll 상태만 로깅.
  // state는 자식 컴포넌트(GoodsCarousel 등)가 같은 윈도우 동안 자체 emit을
  // 보류하도록 prop chain으로 전달하기 위함.
  const awaitDataChangeRef = useRef(false);
  const [isAwaitingDataChange, setIsAwaitingDataChange] = useState(false);
  const visibleComponentIdsRef = useRef<Set<number>>(new Set());
  const [visibleComponentIds, setVisibleComponentIds] = useState<Set<number>>(
    () => new Set(),
  );

  /** 중복 방지 + DISPLAY 이벤트 + 선택적 AdEvent 전송. */
  const emitDisplayLog = ({
    logKey,
    name,
    analytics,
    ably_only_log,
    ad,
  }: {
    logKey: string;
    name: DisplayEventName;
    analytics: Record<string, unknown>;
    ably_only_log?: Record<string, unknown> | null;
    ad?: Record<string, unknown> | null;
  }) => {
    if (displayHistoryRef.current.has(logKey)) return;
    displayHistoryRef.current.add(logKey);

    bridge.logAnalyticsEvent({
      name,
      analytics,
      ably_only_log: ably_only_log ?? undefined,
    });

    if (isNotNil(ad)) {
      bridge.logAdEvent({ action: AdAction.DISPLAY, ad });
    }
  };

  // 매 렌더마다 최신 클로저(displayItems / logDisabled / screen)를 ref에 반영 —
  // coalescing setTimeout 콜백에서 stale closure로 오래된 값을 읽지 않도록.
  const runCheckRef = useRef<() => void>(() => {});
  runCheckRef.current = () => {
    const viewportHeight = viewportHeightRef.current?.height ?? 0;
    if (viewportHeight === 0) return;
    const viewportTop = scrollYRef.current;
    const viewportBottom = viewportTop + viewportHeight;

    const nextVisibleIds = new Set<number>();
    let offset = 0;

    for (let i = 0; i < displayItems.length; i++) {
      const item = displayItems[i];
      const height = itemHeights.current?.[i];

      // 미측정 cell을 만나면 이후 offset이 부정확하므로 탐색 중단.
      // (미측정 cell은 대개 viewport 밖이라 판정 대상도 아님.)
      if (height === undefined || height <= 0) break;

      const itemTop = offset;
      const itemBottom = offset + height;
      offset = itemBottom;

      if (itemTop >= viewportBottom) break;
      if (itemBottom <= viewportTop) continue;

      const visibleTop = Math.max(itemTop, viewportTop);
      const visibleBottom = Math.min(itemBottom, viewportBottom);
      const visibilityRatio = (visibleBottom - visibleTop) / height;
      if (visibilityRatio < thresholdRatio) continue;

      // autoScroll gate는 logDisabled와 무관하게 항상 유지
      if (item.kind === 'component') {
        nextVisibleIds.add(item.component.componentId);
      }

      if (logDisabled || awaitDataChangeRef.current) continue;
      if (item.kind !== 'row-3col' && item.kind !== 'row-2col') continue;

      for (const rowItem of item.items) {
        switch (rowItem.type) {
          case 'goods': {
            if (!rowItem.logging) break;
            emitDisplayLog({
              logKey: `goods-${rowItem.sno}`,
              name: 'DISPLAY_GOODS',
              analytics: {
                ...(isNotNil(screen) && { SCREEN: screen }),
                ad: rowItem.ad,
                ...rowItem.logging.analytics,
              },
              ably_only_log: rowItem.logging.ably_only_log,
              ad: rowItem.ad,
            });
            break;
          }
          case 'videoGoods': {
            emitDisplayLog({
              logKey: `video-${rowItem.sno}`,
              name: 'DISPLAY_GOODS',
              analytics: {
                CARD_TYPE: 'VIDEO',
                ...rowItem.logging.analytics,
                ...(isNotNil(screen) && { SCREEN: screen }),
              },
              ably_only_log: rowItem.logging.ably_only_log,
              ad: rowItem.item.ad,
            });
            break;
          }
          case 'primaryCard': {
            emitDisplayLog({
              logKey: `primary-${rowItem.key}`,
              name: 'DISPLAY_PRIMARY_CARD',
              analytics: {
                ...(isNotNil(screen) && { SCREEN: screen }),
                ...rowItem.logging.analytics,
              },
              ably_only_log: rowItem.logging.ably_only_log,
            });
            break;
          }
          case 'placeholder':
            break;
        }
      }
    }

    if (!setsEqual(visibleComponentIdsRef.current, nextVisibleIds)) {
      visibleComponentIdsRef.current = nextVisibleIds;
      setVisibleComponentIds(nextVisibleIds);
    }
  };

  // scroll/layout/items 변경이 한 프레임에 몰려도 coalescing으로 한 번만 평가.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleCheck = useCallback(() => {
    if (timerRef.current !== null) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runCheckRef.current();
    }, COALESCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // unblock 조건: 데이터 fetch 완료 + scrollToFilterList dispatch 완료 + scroll
  // 애니메이션 버퍼. 고정 1.5초 timer는 slow API에서 fetch가 길어지면 fetch 완료
  // 전에 풀려 emit이 새는 문제가 있어, 실제 fetch/scroll 상태에 묶음.
  const UNBLOCK_BUFFER_MS = 500;

  // resetDisplayHistory는 in-render setState로도 호출 가능해야 한다(필터 변경
  // 즉시 자식까지 awaiting=true를 보장하기 위함). 타이머/사이드이펙트는 useEffect
  // 로 분리하고 여기서는 state 갱신만 수행. resetTrigger는 같은 awaiting=true
  // 윈도우에서 다시 reset이 호출돼도 unblock effect가 재실행되도록 trigger 역할.
  const [resetTrigger, setResetTrigger] = useState(0);

  const resetDisplayHistory = useCallback(() => {
    displayHistoryRef.current.clear();
    awaitDataChangeRef.current = true;
    setIsAwaitingDataChange(true);
    setResetTrigger(t => t + 1);
  }, []);

  useEffect(() => {
    if (!isAwaitingDataChange) return;
    // fetch 진행 중이거나 scroll dispatch 전이면 unblock 보류.
    if (isFetching || isPendingScroll) return;
    // 데이터/scroll 모두 정착. scroll 애니메이션 버퍼 후 release.
    const timer = setTimeout(() => {
      awaitDataChangeRef.current = false;
      setIsAwaitingDataChange(false);
      scheduleCheck();
    }, UNBLOCK_BUFFER_MS);
    return () => clearTimeout(timer);
  }, [
    isAwaitingDataChange,
    isFetching,
    isPendingScroll,
    resetTrigger,
    scheduleCheck,
  ]);

  useEffect(() => {
    scheduleCheck();
  }, [displayItems, scheduleCheck]);

  const updateScrollY = useCallback(
    (y: number) => {
      scrollYRef.current = y;
      scheduleCheck();
    },
    [scheduleCheck],
  );

  return {
    visibleComponentIds,
    isAwaitingDataChange,
    scheduleCheck,
    updateScrollY,
    resetDisplayHistory,
  };
};
